import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { clipboardSafeUsername } from '../lib/wingo-username';

type ClipboardStatus =
  | 'pending'
  | 'approved_to_sheet'
  | 'ignored'
  | 'duplicate'
  | 'needs_review';

type ApprovalSource = 'auto' | 'manual';

interface ClipboardRow {
  id: number;
  date: string;
  activityDateIso: string;
  username: string;
  athleteId?: number;
  wingos: number;
  activityLink: string;
  distanceKm: number | null;
  status: ClipboardStatus;
  googleSheetRowNumber: number | null;
  sheetLedgerId: string | null;
  approvalSource: ApprovalSource | null;
  approvedAt: string | null;
  preInitiationWingos: number;
  beforeConnectionWingos: number;
  connectedAt: string | null;
  eligibilityStartAt: string | null;
  includePreInitiationWingos: boolean;
  companionSources?: { sourceUsername: string; tag: string; activityId: number }[];
  duplicateMatch: {
    reason: string;
    sheetLedgerEntryId: string | null;
    sheetEntrySource?: 'auto' | 'manual' | null;
  } | null;
}

interface PreviewResponse {
  willWrite?: boolean;
  alreadyApproved: boolean;
  isHistorical: boolean;
  requiresHistoricalConfirm: boolean;
  duplicateMatch: { sheetLedgerEntryId: string | null } | null;
  connectedAt?: string | null;
  eligibilityStartAt?: string | null;
  beforeConnectionWingos?: number;
  detectedWingos?: number;
  preInitiationWingos?: number;
  includedWingos?: number;
  includePreInitiationWingos?: boolean;
  preview: {
    targetSheetRow: number;
    ledgerEntryId: number;
    username: string;
    date: string;
    wingos: number;
    km: string;
    expectedFullId: string;
    initiation?: boolean;
  } | null;
}

interface ClipboardResponse {
  lastPulledAt: string | null;
  rowCount: number;
  rows: ClipboardRow[];
}

interface AutoApproveSummary {
  written: number;
  duplicates: number;
  skippedHistorical: number;
  skippedUnnamed?: number;
  errors: string[];
}

interface PullSummary {
  processed?: number;
  errors?: number;
  newEfforts?: number;
  errorDetails?: string[];
  warnings?: string[];
  rateLimited?: boolean;
  rateLimitedAthlete?: { athleteId: number; username: string } | null;
  deferred?: number;
  deferredAthletes?: Array<{ athleteId: number; username: string }>;
  autoApprove?: AutoApproveSummary | null;
  countCorrections?: { patched?: number; errors?: string[] } | null;
}

const AUTH_KEY = 'wingo_clipboard_authed';

type ViewFilter = 'queue' | 'logged' | 'all';
type MenuPosition = { right: number; top?: number; bottom?: number };

const STATUS_SHORT: Record<ClipboardStatus, string> = {
  pending: 'Pending',
  approved_to_sheet: 'In log',
  ignored: 'Ignored',
  duplicate: 'Dup',
  needs_review: 'Review',
};

const STATUS_CLASSES: Record<ClipboardStatus, string> = {
  pending: 'bg-amber-100 text-amber-900',
  approved_to_sheet: 'bg-emerald-100 text-emerald-900',
  ignored: 'bg-stone-200 text-stone-600',
  duplicate: 'bg-orange-100 text-orange-900',
  needs_review: 'bg-sky-100 text-sky-900',
};

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

function displayClipboardUsername(row: { username: string; athleteId?: number }): string {
  return clipboardSafeUsername(row.username, row.athleteId ?? 0);
}

function logSourceLabel(row: ClipboardRow): string | null {
  if (row.status === 'approved_to_sheet') {
    return row.approvalSource === 'auto' ? 'Auto' : row.approvalSource === 'manual' ? 'Manual' : 'In log';
  }
  if (row.status === 'duplicate') {
    const src = row.duplicateMatch?.sheetEntrySource;
    if (src === 'auto') return 'Sheet · auto';
    if (src === 'manual') return 'Sheet · manual';
    return 'In sheet';
  }
  return null;
}

function canActOn(row: ClipboardRow): boolean {
  return row.status === 'pending' || row.status === 'needs_review';
}

const Clipboard: React.FC = () => {
  usePageTitle('WINGO Clipboard');
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [rows, setRows] = useState<ClipboardRow[]>([]);
  const [lastPulledAt, setLastPulledAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [syncWarning, setSyncWarning] = useState('');
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const [previewRowId, setPreviewRowId] = useState<number | null>(null);
  const [previewData, setPreviewData] = useState<PreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [includePreInitiationWingos, setIncludePreInitiationWingos] = useState(false);
  const [eligibilityDate, setEligibilityDate] = useState('');
  const [savingEligibility, setSavingEligibility] = useState(false);
  const [actionRowId, setActionRowId] = useState<number | null>(null);
  const [viewFilter, setViewFilter] = useState<ViewFilter>('queue');
  const [error, setError] = useState('');

  const fetchLogs = useCallback(async (pw: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/clipboard-logs?limit=60&days=90', {
        headers: { 'X-Clipboard-Password': pw },
        cache: 'no-store',
      });
      if (res.status === 401) {
        sessionStorage.removeItem(AUTH_KEY);
        setIsAuthenticated(false);
        setError('Wrong password.');
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || `HTTP ${res.status}`);
      }
      const data = (await res.json()) as ClipboardResponse;
      setRows(data.rows);
      setLastPulledAt(data.lastPulledAt);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem(AUTH_KEY);
    if (saved) {
      setIsAuthenticated(true);
      fetchLogs(saved);
    }
  }, [fetchLogs]);

  const filteredRows = useMemo(() => {
    if (viewFilter === 'all') return rows;
    if (viewFilter === 'logged') {
      return rows.filter((r) => r.status === 'approved_to_sheet' || r.status === 'duplicate');
    }
    return rows.filter((r) => r.status === 'pending' || r.status === 'needs_review');
  }, [rows, viewFilter]);

  const counts = useMemo(() => ({
    queue: rows.filter((r) => r.status === 'pending' || r.status === 'needs_review').length,
    logged: rows.filter((r) => r.status === 'approved_to_sheet' || r.status === 'duplicate').length,
    all: rows.length,
  }), [rows]);

  const postAction = async (body: Record<string, unknown>) => {
    const saved = sessionStorage.getItem(AUTH_KEY);
    if (!saved) return null;
    const res = await fetch('/api/clipboard-action', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Clipboard-Password': saved,
      },
      body: JSON.stringify(body),
    });
    const responseBody = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(responseBody.message || responseBody.error || `HTTP ${res.status}`);
    }
    return responseBody;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;
    sessionStorage.setItem(AUTH_KEY, password);
    setIsAuthenticated(true);
    fetchLogs(password);
  };

  const handleSync = async () => {
    const saved = sessionStorage.getItem(AUTH_KEY);
    if (!saved) return;

    setSyncing(true);
    setSyncMessage('');
    setSyncWarning('');
    setError('');
    try {
      const res = await fetch('/api/cron/pull-wingos', { cache: 'no-store' });
      const body = (await res.json().catch(() => ({}))) as PullSummary & { error?: string };
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);

      const parts: string[] = [];
      if (typeof body.processed === 'number') {
        parts.push(`${body.processed} athletes`);
      }
      if (typeof body.newEfforts === 'number') {
        parts.push(`${body.newEfforts} new Wingo${body.newEfforts === 1 ? '' : 's'}`);
      }
      const auto = body.autoApprove as AutoApproveSummary | null;
      if (auto?.written) parts.push(`${auto.written} auto-logged`);
      if (auto?.duplicates) parts.push(`${auto.duplicates} dupes`);
      if (auto?.skippedUnnamed) parts.push(`${auto.skippedUnnamed} unnamed held`);
      if (body.countCorrections?.patched) {
        parts.push(`${body.countCorrections.patched} count fix${body.countCorrections.patched === 1 ? '' : 'es'}`);
      }
      if (body.errors && !body.rateLimited) parts.push(`${body.errors} pull errors`);
      setSyncMessage(parts.length ? parts.join(' · ') : 'Sync complete');

      if (body.rateLimited) {
        const stoppedAt = body.rateLimitedAthlete?.username || 'an athlete';
        const deferredNames = body.deferredAthletes?.map((athlete) => athlete.username) || [];
        const deferredText = deferredNames.length > 0
          ? ` Not checked: ${deferredNames.join(', ')}.`
          : '';
        setSyncWarning(
          `Strava's shared API limit was reached while checking ${stoppedAt}. ` +
          `${body.deferred || 0} athlete${body.deferred === 1 ? '' : 's'} deferred; ` +
          `syncing again immediately will not help.${deferredText}`
        );
      } else if (body.errorDetails?.length) {
        setSyncWarning(body.errorDetails.join(' '));
      } else if (body.warnings?.length) {
        setSyncWarning(body.warnings.join(' '));
      }

      await fetchLogs(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const runRowAction = async (
    id: number,
    action: 'ignore' | 'needs_review' | 'pending',
  ) => {
    const saved = sessionStorage.getItem(AUTH_KEY);
    if (!saved) return;
    setOpenMenuId(null);
    setMenuPosition(null);
    setActionRowId(id);
    setError('');
    try {
      await postAction({ id, action });
      await fetchLogs(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionRowId(null);
    }
  };

  const loadWritePreview = async (id: number, includePreInitiation: boolean) => {
    setPreviewData(null);
    setPreviewLoading(true);
    setError('');
    try {
      const body = await postAction({
        id,
        action: 'preview',
        includePreInitiationWingos: includePreInitiation,
      });
      setPreviewData(body as PreviewResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview failed');
      setPreviewRowId(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const openWritePreview = async (id: number) => {
    setOpenMenuId(null);
    setMenuPosition(null);
    setPreviewRowId(id);
    setIncludePreInitiationWingos(false);
    const row = rows.find((item) => item.id === id);
    setEligibilityDate(row?.eligibilityStartAt?.slice(0, 10) ?? '');
    await loadWritePreview(id, false);
  };

  const saveEligibilityDate = async () => {
    if (previewRowId == null) return;
    const saved = sessionStorage.getItem(AUTH_KEY);
    if (!saved) return;
    setSavingEligibility(true);
    setError('');
    try {
      await postAction({
        id: previewRowId,
        action: 'set_eligibility_date',
        eligibilityStartAt: eligibilityDate || null,
      });
      await fetchLogs(saved);
      setIncludePreInitiationWingos(false);
      await loadWritePreview(previewRowId, false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save eligibility date');
    } finally {
      setSavingEligibility(false);
    }
  };

  const handlePreInitiationToggle = (checked: boolean) => {
    if (previewRowId == null) return;
    setIncludePreInitiationWingos(checked);
    void loadWritePreview(previewRowId, checked);
  };

  const confirmWrite = async () => {
    if (previewRowId == null) return;
    const saved = sessionStorage.getItem(AUTH_KEY);
    if (!saved) return;

    setActionRowId(previewRowId);
    setError('');
    try {
      await postAction({
        id: previewRowId,
        action: 'approve',
        confirm: true,
        includePreInitiationWingos,
      });
      setPreviewRowId(null);
      setPreviewData(null);
      setIncludePreInitiationWingos(false);
      await fetchLogs(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Write failed');
    } finally {
      setActionRowId(null);
    }
  };

  const toggleRowMenu = (id: number, button: HTMLButtonElement) => {
    if (openMenuId === id) {
      setOpenMenuId(null);
      setMenuPosition(null);
      return;
    }

    const rect = button.getBoundingClientRect();
    const openAbove = window.innerHeight - rect.bottom < 190 && rect.top > 190;
    setMenuPosition({
      right: Math.max(8, window.innerWidth - rect.right),
      ...(openAbove
        ? { bottom: Math.max(8, window.innerHeight - rect.top + 6) }
        : { top: rect.bottom + 6 }),
    });
    setOpenMenuId(id);
  };

  useEffect(() => {
    if (openMenuId == null) return;
    const closeMenu = () => {
      setOpenMenuId(null);
      setMenuPosition(null);
    };
    window.addEventListener('resize', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    return () => {
      window.removeEventListener('resize', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, [openMenuId]);

  const openMenuRow = openMenuId == null ? null : rows.find((row) => row.id === openMenuId) ?? null;

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-white border border-stone-200 rounded-lg p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-stone-900">WINGO Clipboard</h1>
          <p className="mt-1 text-sm text-stone-500">Strava mining queue</p>
          <form onSubmit={handleSubmit} className="mt-6 space-y-3">
            <input
              id="clipboard-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full border border-stone-300 rounded px-3 py-2 text-sm"
              autoComplete="current-password"
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              className="w-full bg-stone-900 text-white text-sm font-medium py-2 rounded hover:bg-stone-800"
            >
              Enter
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 text-stone-900">
      <div className="max-w-3xl mx-auto px-3 sm:px-4 py-5 sm:py-8">
        <header className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold tracking-tight">Clipboard</h1>
            <p className="text-xs sm:text-sm text-stone-500 mt-0.5">
              Auto-logs new Wingos on sync · last pull {formatWhen(lastPulledAt)}
            </p>
          </div>
          <button
            type="button"
            onClick={handleSync}
            disabled={syncing || loading}
            className="shrink-0 text-sm font-medium bg-stone-900 text-white px-3 py-1.5 rounded hover:bg-stone-800 disabled:opacity-50"
          >
            {syncing ? 'Syncing…' : 'Sync'}
          </button>
        </header>

        {syncMessage && (
          <p className="mb-3 text-xs sm:text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5">
            {syncMessage}
          </p>
        )}

        {syncWarning && (
          <p className="mb-3 text-xs sm:text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
            {syncWarning}
          </p>
        )}

        {error && (
          <p className="mb-3 text-xs sm:text-sm text-red-700 bg-red-50 border border-red-100 rounded px-2.5 py-1.5">
            {error}
          </p>
        )}

        <div className="flex gap-1.5 mb-4">
          {([
            ['queue', `Queue (${counts.queue})`],
            ['logged', `In log (${counts.logged})`],
            ['all', `Recent (${counts.all})`],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setViewFilter(key)}
              className={`text-xs sm:text-sm px-2.5 py-1 rounded-full border ${
                viewFilter === key
                  ? 'bg-stone-900 text-white border-stone-900'
                  : 'bg-white text-stone-600 border-stone-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading && rows.length === 0 ? (
          <p className="text-sm text-stone-500">Loading…</p>
        ) : filteredRows.length === 0 ? (
          <p className="text-sm text-stone-500 py-8 text-center bg-white rounded-lg border border-stone-200">
            {viewFilter === 'queue' ? 'Queue empty — new Wingos appear after sync.' : 'Nothing here.'}
          </p>
        ) : (
          <div className="bg-white border border-stone-200 rounded-lg shadow-sm divide-y divide-stone-100">
            {filteredRows.map((row) => {
              const source = logSourceLabel(row);
              const busy = actionRowId === row.id;
              return (
                <div key={row.id} className="px-3 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{displayClipboardUsername(row)}</span>
                      <span className={`text-[10px] sm:text-xs px-1.5 py-0.5 rounded ${STATUS_CLASSES[row.status]}`}>
                        <span className="sm:hidden">{STATUS_SHORT[row.status]}</span>
                        <span className="hidden sm:inline">
                          {row.status === 'approved_to_sheet' ? 'In log' : STATUS_SHORT[row.status]}
                        </span>
                      </span>
                      {source && (
                        <span className="text-[10px] sm:text-xs text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded">
                          {source}
                        </span>
                      )}
                      {(row.preInitiationWingos > 0 || row.beforeConnectionWingos > 0) && (
                        <span className="text-[10px] sm:text-xs text-violet-800 bg-violet-100 px-1.5 py-0.5 rounded">
                          {row.preInitiationWingos > 0
                            ? `Pre-init · ${row.preInitiationWingos} ${row.status === 'approved_to_sheet' ? 'detected' : 'held'}`
                            : `Before connection · ${row.beforeConnectionWingos}`}
                        </span>
                      )}
                    </div>
                    {!!row.companionSources?.length && (
                      <div className="mt-1 text-xs text-sky-900">
                        Companion · manual review · {row.companionSources.map((source) => (
                          <a key={`${source.activityId}-${source.tag}-${source.sourceUsername}`} href={`https://strava.com/activities/${source.activityId}`} target="_blank" rel="noopener noreferrer" className="underline mr-2">
                            {source.sourceUsername} · with {source.tag}
                          </a>
                        ))}
                      </div>
                    )}
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-stone-500 tabular-nums">
                      <span>{row.date}</span>
                      <span className="font-medium text-stone-800">+{row.wingos}</span>
                      {row.distanceKm != null && (
                        <span className="hidden sm:inline">{row.distanceKm.toFixed(1)} km</span>
                      )}
                      {row.sheetLedgerId && (
                        <span className="hidden sm:inline">ID {row.sheetLedgerId}</span>
                      )}
                      {row.activityLink && (
                        <a
                          href={row.activityLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-amber-800 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Strava
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="shrink-0">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={(event) => toggleRowMenu(row.id, event.currentTarget)}
                      className="w-8 h-8 flex items-center justify-center rounded border border-stone-200 text-stone-600 hover:bg-stone-50 disabled:opacity-40"
                      aria-label={`Actions for ${displayClipboardUsername(row)}`}
                      aria-haspopup="menu"
                      aria-expanded={openMenuId === row.id}
                    >
                      {busy ? '…' : '⋯'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-4 text-[10px] sm:text-xs text-stone-400 text-center">
          Showing last 90 days · max 60 rows
        </p>
      </div>

      {openMenuRow && menuPosition && createPortal(
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default"
            aria-label="Close actions menu"
            onClick={() => { setOpenMenuId(null); setMenuPosition(null); }}
          />
          <div
            role="menu"
            aria-label={`Actions for ${displayClipboardUsername(openMenuRow)}`}
            className="fixed z-50 min-w-[10rem] bg-white border border-stone-200 rounded-lg shadow-lg py-1 text-sm"
            style={menuPosition}
          >
            {openMenuRow.activityLink && (
              <a
                role="menuitem"
                href={openMenuRow.activityLink}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-left px-3 py-2 hover:bg-stone-50"
                onClick={() => { setOpenMenuId(null); setMenuPosition(null); }}
              >
                Open Strava
              </a>
            )}
            {canActOn(openMenuRow) && (
              <button
                type="button"
                role="menuitem"
                className="w-full text-left px-3 py-2 hover:bg-stone-50"
                onClick={() => openWritePreview(openMenuRow.id)}
              >
                Write to log
              </button>
            )}
            {openMenuRow.status === 'pending' && (
              <button
                type="button"
                role="menuitem"
                className="w-full text-left px-3 py-2 hover:bg-stone-50"
                onClick={() => runRowAction(openMenuRow.id, 'needs_review')}
              >
                Flag review
              </button>
            )}
            {canActOn(openMenuRow) && (
              <button
                type="button"
                role="menuitem"
                className="w-full text-left px-3 py-2 hover:bg-stone-50 text-red-700"
                onClick={() => runRowAction(openMenuRow.id, 'ignore')}
              >
                Reject
              </button>
            )}
            {(openMenuRow.status === 'ignored' || openMenuRow.status === 'needs_review') && (
              <button
                type="button"
                role="menuitem"
                className="w-full text-left px-3 py-2 hover:bg-stone-50"
                onClick={() => runRowAction(openMenuRow.id, 'pending')}
              >
                Re-queue
              </button>
            )}
          </div>
        </>,
        document.body,
      )}

      {previewRowId != null && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-0 sm:px-4">
          <div className="w-full sm:max-w-md bg-white rounded-t-xl sm:rounded-lg shadow-lg border border-stone-200 p-4 sm:p-5 max-h-[85vh] overflow-y-auto">
            <h2 className="text-base font-semibold">Write to Log</h2>

            {previewLoading ? (
              <p className="text-sm text-stone-500 mt-3">Loading…</p>
            ) : previewData?.duplicateMatch ? (
              <p className="text-sm text-orange-800 mt-3">
                Already in sheet (Log ID {previewData.duplicateMatch.sheetLedgerEntryId || '—'}).
              </p>
            ) : previewData?.preview ? (
              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
                <dt className="text-stone-500">User</dt>
                <dd>{clipboardSafeUsername(previewData.preview.username, 0)}</dd>
                <dt className="text-stone-500">Date</dt>
                <dd>{previewData.preview.date}</dd>
                <dt className="text-stone-500">Wingos to log</dt>
                <dd className="font-medium">+{previewData.preview.wingos}</dd>
                {(previewData.preInitiationWingos ?? 0) > 0 && (
                  <>
                    <dt className="text-stone-500">Detected</dt>
                    <dd>{previewData.detectedWingos}</dd>
                    <dt className="text-stone-500">Pre-init held</dt>
                    <dd className="text-violet-700">{previewData.preInitiationWingos}</dd>
                    <dt className="text-stone-500">Eligible from</dt>
                    <dd>{formatWhen(previewData.eligibilityStartAt ?? null)}</dd>
                  </>
                )}
                {previewData.preview.initiation ? (
                  <>
                    <dt className="text-stone-500">Initiation</dt>
                    <dd>Yes · first eligible Wingo is DQ’d</dd>
                  </>
                ) : null}
                <dt className="text-stone-500">Log ID</dt>
                <dd>{previewData.preview.ledgerEntryId}</dd>
                <dt className="text-stone-500">Row</dt>
                <dd>{previewData.preview.targetSheetRow}</dd>
              </dl>
            ) : (
              <p className="text-sm text-stone-500 mt-3">No preview.</p>
            )}

            {previewData?.preview && (
              <div className="mt-4 rounded-lg border border-stone-200 p-3">
                <label htmlFor="eligibility-date" className="block text-sm font-medium text-stone-900">
                  Mining eligible from
                </label>
                <p className="mt-0.5 text-xs text-stone-500">
                  Admin-set initiation date. Strava connection ({formatWhen(previewData.connectedAt ?? null)}) is only a clue, not approval.
                </p>
                <div className="mt-2 flex gap-2">
                  <input
                    id="eligibility-date"
                    type="date"
                    value={eligibilityDate}
                    onChange={(event) => setEligibilityDate(event.target.value)}
                    className="min-w-0 flex-1 rounded border border-stone-300 px-2.5 py-1.5 text-sm"
                  />
                  <button
                    type="button"
                    disabled={savingEligibility}
                    onClick={saveEligibilityDate}
                    className="rounded border border-stone-300 px-3 py-1.5 text-sm font-medium hover:bg-stone-50 disabled:opacity-50"
                  >
                    {savingEligibility ? 'Saving…' : 'Apply'}
                  </button>
                </div>
              </div>
            )}

            {previewData?.includedWingos === 0 && (
              <p className="mt-4 text-sm text-violet-900 bg-violet-50 border border-violet-200 rounded-lg p-3">
                Nothing is eligible to log. All detected Wingos occurred before the admin-set eligibility date.
              </p>
            )}

            {(previewData?.beforeConnectionWingos ?? 0) > 0 && (previewData?.preInitiationWingos ?? 0) === 0 && (
              <p className="mt-4 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
                {previewData?.beforeConnectionWingos} Wingo{previewData?.beforeConnectionWingos === 1 ? '' : 's'} occurred before Strava was connected. This is a review clue only—not the initiation cutoff.
              </p>
            )}

            {(previewData?.preInitiationWingos ?? 0) > 0 && !previewData?.duplicateMatch && (
              <label className="mt-4 flex items-start gap-3 rounded-lg border border-stone-200 bg-stone-50 p-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includePreInitiationWingos}
                  disabled={previewLoading || actionRowId === previewRowId}
                  onChange={(event) => handlePreInitiationToggle(event.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-emerald-600"
                />
                <span>
                  <span className="block text-sm font-medium text-stone-900">
                    Include {previewData?.preInitiationWingos} pre-initiation Wingo{previewData?.preInitiationWingos === 1 ? '' : 's'}
                  </span>
                  <span className="block mt-0.5 text-xs text-stone-500">
                    Off by default. These were detected before the admin-set eligibility date and are excluded from the Log.
                  </span>
                </span>
              </label>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setPreviewRowId(null);
                  setPreviewData(null);
                  setIncludePreInitiationWingos(false);
                }}
                className="text-sm px-3 py-1.5 rounded border border-stone-300"
              >
                Cancel
              </button>
              {previewData?.preview && !previewData.duplicateMatch && (
                <button
                  type="button"
                  disabled={actionRowId === previewRowId || previewData.includedWingos === 0}
                  onClick={confirmWrite}
                  className="text-sm px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {actionRowId === previewRowId ? 'Writing…' : 'Confirm'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clipboard;
